import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Hash, Lock, MailPlus, RefreshCw, Trash2, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { api, errMsg } from "@/lib/api";
import { copyText, fmtRelative } from "@/lib/format";
import { analytics } from "@/lib/analytics";

const parseEmails = (text) => Array.from(new Set((text.match(/[^\s,;<>]+@[^\s,;<>]+\.[^\s,;<>]+/g) || []).map((e) => e.toLowerCase())));

export const InviteDialog = ({ open, onClose, onDone }) => {
  const { workspace } = useAuth();
  const roles = workspace?.permission_catalog?.roles || { member: { label: "Member" }, guest: { label: "Guest" } };
  const [text, setText] = useState("");
  const [role, setRole] = useState("member");
  const [channels, setChannels] = useState([]);
  const [picked, setPicked] = useState([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const emails = useMemo(() => parseEmails(text), [text]);
  useEffect(() => { if (open) { api.get("/conversations/browse").then((r) => setChannels(r.data.filter((c) => !c.is_default))).catch(() => {}); setResult(null); } }, [open]);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invites", { emails, role, channels: picked, message, app_url: window.location.origin });
      setResult(data);
      analytics.track("admin_invites_sent", { category: "admin", target: "invite", metadata: { count: data.invited, already: data.already_members, role, channels: picked.length } });
      if (data.invited) toast.success(`${data.invited} invitation${data.invited === 1 ? "" : "s"} sent`);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reset = () => { setText(""); setPicked([]); setMessage(""); setResult(null); };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="tc-dialog" style={{ maxWidth: 560 }} data-testid="invite-dialog">
        <DialogTitle>Invite people to {workspace?.name}</DialogTitle>
        <DialogDescription>Only invited people can sign in. Each person gets an email with a link; they sign in with a one-time code — no password.</DialogDescription>
        {!result ? (
          <>
            <label className="tc-label">Email addresses</label>
            <textarea className="tc-textarea" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="name@company.com, another@company.com — paste a list, one per line or comma-separated" data-testid="invite-emails" autoFocus />
            {emails.length > 0 && <div className="muted" style={{ fontSize: 12, marginTop: 4 }} data-testid="invite-email-count">{emails.length} address{emails.length === 1 ? "" : "es"} recognized{emails.length > 50 ? " — maximum 50 at a time" : ""}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
              <div>
                <label className="tc-label">Role</label>
                <select className="tc-select" style={{ width: "100%" }} value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role">
                  {Object.entries(roles).filter(([r]) => r !== "bot").map(([r, d]) => <option key={r} value={r}>{d.label}</option>)}
                </select>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{role === "guest" ? "Limited: DMs, reactions, files, joining huddles." : "Standard access to channels, DMs, huddles and files."} You can fine-tune per person later.</div>
              </div>
              <div>
                <label className="tc-label">Add to channels <span className="muted">(besides defaults)</span></label>
                <div className="tc-checklist tc-scroll" data-testid="invite-channels">
                  {channels.map((c) => (
                    <label key={c.id} className="tc-check"><input type="checkbox" checked={picked.includes(c.id)} onChange={(e) => setPicked((p) => (e.target.checked ? [...p, c.id] : p.filter((x) => x !== c.id)))} data-testid={`invite-channel-${c.name}`} />{c.is_private ? <Lock size={12} /> : <Hash size={12} />} {c.name}</label>
                  ))}
                  {!channels.length && <span className="muted" style={{ fontSize: 12 }}>Default channels only</span>}
                </div>
              </div>
            </div>
            <label className="tc-label" style={{ marginTop: 14 }}>Personal note <span className="muted">(optional)</span></label>
            <textarea className="tc-textarea" rows={2} value={message} onChange={(e) => setMessage(e.target.value.slice(0, 500))} placeholder="Welcome aboard! We use #claims for day-to-day questions." data-testid="invite-message" />
            <div className="tc-dialog__footer">
              <button className="tc-btn tc-btn--outline" onClick={onClose} data-testid="invite-cancel">Cancel</button>
              <button className="tc-btn tc-btn--primary" disabled={!emails.length || emails.length > 50 || busy} onClick={send} data-testid="invite-send"><MailPlus size={14} /> {busy ? "Sending…" : `Send ${emails.length || ""} invitation${emails.length === 1 ? "" : "s"}`}</button>
            </div>
          </>
        ) : (
          <>
            <div className="tc-invite-results" data-testid="invite-results">
              {result.results.map((r) => (
                <div key={r.email} className={`tc-invite-result is-${r.status}`} data-testid={`invite-result-${r.email}`}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="mono" style={{ fontSize: 13 }}>{r.email}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {r.status === "already_member" ? `Already a member (${r.user.display_name}) — nothing sent.` : r.invite.delivered ? "Invitation emailed." : `Email couldn't be sent${r.invite.delivery_error ? ` (${r.invite.delivery_error})` : ""} — share the link instead.`}
                    </div>
                  </div>
                  {r.status === "invited" && <button className="tc-btn tc-btn--ghost sm" onClick={() => { copyText(r.invite.link); toast.success("Invite link copied"); }} data-testid={`invite-copy-${r.email}`}><Copy size={13} /> Copy link</button>}
                </div>
              ))}
            </div>
            <div className="tc-dialog__footer">
              <button className="tc-btn tc-btn--outline" onClick={reset} data-testid="invite-more">Invite more</button>
              <button className="tc-btn tc-btn--primary" onClick={onClose} data-testid="invite-done">Done</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const PendingInvites = ({ refreshKey }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/invites").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const resend = async (inv) => {
    setBusy(inv.id);
    try { const { data } = await api.post(`/admin/invites/${inv.id}/resend`, { app_url: window.location.origin }); toast.success(data.delivered ? `Invitation re-sent to ${inv.email}` : "Email couldn't be sent — copy the link instead"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const revoke = async (inv) => {
    if (!window.confirm(`Revoke the invitation for ${inv.email}? They won't be able to sign in unless invited again.`)) return;
    setBusy(inv.id);
    try { await api.delete(`/admin/invites/${inv.id}`); toast.success("Invitation revoked"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  if (!rows?.length) return null;
  return (
    <div className="tc-admin-card" data-testid="pending-invites">
      <div className="tc-admin-card__head"><UserPlus size={16} /> Pending invitations <span className="right">{rows.length}</span></div>
      <div style={{ overflowX: "auto" }}>
        <table className="tc-table" data-testid="pending-invites-table">
          <thead><tr><th>Email</th><th>Role</th><th>Invited by</th><th>Sent</th><th>Expires</th><th>Status</th><th /></tr></thead>
          <tbody>
            {rows.map((inv) => (
              <tr key={inv.id} data-testid={`invite-row-${inv.email}`} className={inv.status === "expired" ? "is-dim" : ""}>
                <td className="mono">{inv.email}</td>
                <td><span className="tc-pill">{inv.role === "guest" ? "Guest" : "Member"}</span></td>
                <td className="muted">{inv.invited_by_user?.display_name || "—"}</td>
                <td className="muted" style={{ whiteSpace: "nowrap" }}>{inv.last_sent_at ? fmtRelative(inv.last_sent_at) : "—"}{inv.sent_count > 1 ? ` · ${inv.sent_count}×` : ""}</td>
                <td className="muted" style={{ whiteSpace: "nowrap" }}>{inv.expires_at ? fmtRelative(inv.expires_at) : "—"}</td>
                <td>{inv.status === "expired" ? <span className="tc-pill danger">Expired</span> : inv.delivered === false ? <span className="tc-pill danger" title={inv.delivery_error || ""}>Email failed</span> : <span className="tc-pill client">Pending</span>}</td>
                <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                  <button className="tc-iconbtn" title="Copy invite link" onClick={() => { copyText(inv.link); toast.success("Invite link copied"); }} data-testid={`invite-link-${inv.email}`}><Copy size={14} /></button>
                  <button className="tc-iconbtn" title="Resend invitation" disabled={busy === inv.id} onClick={() => resend(inv)} data-testid={`invite-resend-${inv.email}`}><RefreshCw size={14} /></button>
                  <button className="tc-iconbtn" title="Revoke invitation" disabled={busy === inv.id} onClick={() => revoke(inv)} data-testid={`invite-revoke-${inv.email}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
